import { EMAIL_CONNECTOR_NAME } from '@/features/campaigns/EmailCampaign/constants';
import { useEmailSendingDomains } from '@/features/campaigns/EmailCampaign/domains/useEmailSendingDomains';
import { useWorkspace } from '@/features/workspaces/hooks';
import { EmailSendingDomain } from '@/models/emailSendingDomains';
import { useCompanyValues } from '@/providers';
import { useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { createContext, FC, PropsWithChildren, useMemo } from 'react';

export type EmailCampaignGate = 'connect-mailtrap' | 'no-audience' | 'no-sending-domain' | 'domain-not-verified' | 'ready';

export type EmailCampaignContextValue = {
  gate: EmailCampaignGate;
  isGateLoading: boolean;
  isMailtrapConnected: boolean;
  isConnectorsLoading: boolean;
  missingAudience: boolean;
  hasSendingDomain: boolean;
  hasVerifiedDomain: boolean;
  domains: EmailSendingDomain[];
  hasNoAvailableDomains: boolean;
  hasFetched: boolean;
  isDomainsLoading: boolean;
  isDomainsFetching: boolean;
  isDomainsError: boolean;
  refetchDomains: () => Promise<void>;
  deleteFlowId?: string;
  connectorInstanceId?: string;
};

export const EmailCampaignContext = createContext<EmailCampaignContextValue | null>(null);

export const EmailCampaignProvider: FC<PropsWithChildren> = ({ children }) => {
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();

  const { data: customConnectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );

  const isMailtrapConnected = customConnectors?.find((connector) => connector.name === EMAIL_CONNECTOR_NAME)?.isAuthenticated ?? false;

  const {
    domains,
    hasNoAvailableDomains,
    hasFetched,
    missingAudience,
    isLoading: isDomainsLoading,
    isFetching: isDomainsFetching,
    isError: isDomainsError,
    refetch,
    deleteFlowId,
    connectorInstanceId,
  } = useEmailSendingDomains({ skip: !workspace.id || !isMailtrapConnected });

  const hasSendingDomain = domains.length > 0;
  const hasVerifiedDomain = domains.some((domain) => domain.status === 'VERIFIED');

  const gate: EmailCampaignGate = !isMailtrapConnected
    ? 'connect-mailtrap'
    : missingAudience
      ? 'no-audience'
      : hasVerifiedDomain
        ? 'ready'
        : hasSendingDomain
          ? 'domain-not-verified'
          : 'no-sending-domain';

  const isGateLoading = isConnectorsLoading || (isMailtrapConnected && !missingAudience && (isDomainsLoading || isDomainsFetching));

  const value = useMemo<EmailCampaignContextValue>(
    () => ({
      gate,
      isGateLoading,
      isMailtrapConnected,
      isConnectorsLoading,
      missingAudience,
      hasSendingDomain,
      hasVerifiedDomain,
      domains,
      hasNoAvailableDomains,
      hasFetched,
      isDomainsLoading,
      isDomainsFetching,
      isDomainsError,
      refetchDomains: refetch,
      deleteFlowId,
      connectorInstanceId,
    }),
    [
      connectorInstanceId,
      deleteFlowId,
      domains,
      gate,
      hasFetched,
      hasNoAvailableDomains,
      hasSendingDomain,
      hasVerifiedDomain,
      isConnectorsLoading,
      isDomainsError,
      isDomainsFetching,
      isDomainsLoading,
      isGateLoading,
      isMailtrapConnected,
      missingAudience,
      refetch,
    ]
  );

  return <EmailCampaignContext.Provider value={value}>{children}</EmailCampaignContext.Provider>;
};
